"use client";

import Dropdown from "@/components/Dropdown";
import { useEffect, useState } from "react";
import { useCompanyContext } from "@/context/CompanyContext";
import apiFetch from "@/lib/fetch";
import { DROPDOWN_DEFAULT_VALUE_KEYS } from "@/@core/constants/dropdown";
import { debounce } from 'lodash';
import { useTranslation } from "@/hooks/translationContext";

export default function DropdownPart({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const [selectedStreet, setSelectedStreet] = useState(t("header-quicksearch-district"));
  const [selectedService, setSelectedService] = useState(t("header-quicksearch-service"));
  const [isStreetOpened, setIsStreetOpened] = useState(false);
  const { setCompanyFilters, companyFilters } = useCompanyContext();
  const [districts, setDistricts] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async (route: "services" | "districts") => {
    const queryParams = new URLSearchParams();

    if (route === "districts" && companyFilters.service?.id) {
      queryParams.append("service", companyFilters.service.id.toString());
    }

    if (route === "services" && companyFilters.district?.id) {
      queryParams.append("district", companyFilters.district.id.toString());
    }

    const queryString = queryParams.toString();
    const url = `company/${route}/${slug}${queryString ? `?${queryString}` : ''}`;

    const response = await apiFetch({ route: url });
    return response.map((item: any) => ({
      id: item.id,
      title: item.descriptions?.[0]?.title || item.title,
    }));
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchData("districts"),
      fetchData("services")
    ]).then(([districtsData, servicesData]) => {
      setDistricts(districtsData);
      setServices(servicesData);
      setIsLoading(false);
    });
  }, [companyFilters.service?.id, companyFilters.district?.id]);

  const debouncedSetFilters = debounce((newFilters) => {
    setCompanyFilters(newFilters);
  }, 300);

  const handleDistrictChange = (id: number | null, title: string | null) => {
    setSelectedStreet(title || t(DROPDOWN_DEFAULT_VALUE_KEYS.DISTRICT));
    debouncedSetFilters({
      ...companyFilters,
      district: { id, title }
    });
  };

  const handleServiceChange = (id: number | null, title: string | null) => {
    setSelectedService(title || t(DROPDOWN_DEFAULT_VALUE_KEYS.SERVICE));
    debouncedSetFilters({
      ...companyFilters,
      service: { id, title }
    });
  };

  useEffect(() => {
    if (!companyFilters.service?.id) {
      setSelectedService(t("header-quicksearch-service"));
    } else {
      setSelectedService(String(companyFilters.service?.title));
    }

    if (!companyFilters.district?.id) {
      setSelectedStreet(t("header-quicksearch-district"));
    } else {
      setSelectedStreet(String(companyFilters.district?.title));
    }
  }, [companyFilters]);

  const isDistrictsEmpty = !isLoading && districts.length === 0;
  const isServicesEmpty = !isLoading && services.length === 0;

  return (
    <>
      <Dropdown
        setIsOpened={setIsStreetOpened}
        title={selectedStreet}
        className="max-md:!h-[48px]"
        parentClassName="md:!w-[278px] w-[calc(50%-4px)]"
        dropdownClassName="!w-[100%] !h-auto"
        disabled={isDistrictsEmpty || isLoading}
      >
        {isLoading ? (
          <div className="p-3 text-sm text-slate-500">Yükleniyor...</div>
        ) : isDistrictsEmpty ? (
          <div className="p-3 text-sm text-slate-500">Semt bulunamadı</div>
        ) : (
          <>
            <button
              role="button"
              className="flex items-start px-2 font-bold py-3 rounded cursor-pointer w-full"
              onClick={() => handleDistrictChange(null, null)}
            >
              <span className="text-sm text-slate-800 font-bold">Tümü</span>
            </button>

            {districts.map((str: { title: string; slug: string; id: number }) => (
              <button
                role="button"
                key={str.id}
                onClick={() => handleDistrictChange(str.id, str.title)}
                className="flex items-start px-2 py-3 rounded cursor-pointer w-full"
              >
                <span className="text-sm text-slate-800 font-bold">
                  {str.title}
                </span>
              </button>
            ))}
          </>
        )}
      </Dropdown>

      <Dropdown
        title={selectedService}
        className="max-md:!h-[48px]"
        parentClassName="md:!w-[278px] w-[calc(50%-4px)]"
        dropdownClassName="!w-[100%] !h-auto"
        disabled={isServicesEmpty || isLoading}
      >
        {isLoading ? (
          <div className="p-3 text-sm text-slate-500">Yükleniyor...</div>
        ) : isServicesEmpty ? (
          <div className="p-3 text-sm text-slate-500">Hizmet bulunamadı</div>
        ) : (
          <>
            <button
              role="button"
              className="flex items-start px-2 border-b border-stone-50 py-3 rounded cursor-pointer w-full"
              onClick={() => handleServiceChange(null, null)}
            >
              <span className="text-sm text-slate-800 font-bold">Tümü</span>
            </button>

            {services.map((service: { title: string; id: number }) => (
              <button
                role="button"
                key={service.id}
                onClick={() => handleServiceChange(service.id, service.title)}
                className="flex items-start px-2 border-b border-stone-50 py-3 rounded cursor-pointer w-full"
              >
                <span className="text-sm text-slate-800 font-bold">
                  {service.title}
                </span>
              </button>
            ))}
          </>
        )}
      </Dropdown>
    </>
  );
}